/**
 * Runtime role display helpers (logged-in user context).
 * Role definitions live in `contentRoles.js` — import from there for labels, permissions, dropdowns.
 */
import {
  getRoleChipClass,
  getRoleSidebarModifier,
  getRoleUILabel,
  normalizeRoleKey,
} from "../constants/contentRoles"

export { normalizeRoleKey }

export function getRoleLabel(role) {
  const label = getRoleUILabel(role)
  if (label) return label
  if (!normalizeRoleKey(role)) return ""
  return "User"
}

export function getSidebarRoleModifier(role) {
  if (!normalizeRoleKey(role)) return "default"
  return getRoleSidebarModifier(role)
}

/** My Profile role pill — uses same modifiers as sidebar, different CSS prefix. */
export function getRoleProfileBadgeClass(role) {
  const modifier = getSidebarRoleModifier(role)
  return modifier === "default" ? "role-badge-default" : `role-badge-${modifier}`
}

const ORG_LEVEL_DISPLAY_ROLES = new Set(["ROLE_OWNER", "ROLE_ADMIN"])

/** Organization Owner/Admin — e.g. Executive Dashboard. */
export function canAccessExecutiveDashboard(organizationRole) {
  const key = normalizeRoleKey(organizationRole)
  return ORG_LEVEL_DISPLAY_ROLES.has(key)
}

/**
 * True while org membership role is still loading — avoid showing property role first
 * (e.g. Account Manager) then switching to Owner after org role API returns.
 */
export function isOrganizationRolePending({
  hasUserId = false,
  organizationsStatus = "idle",
  userOrgRoleStatus = "idle",
  organizationCount = 0,
} = {}) {
  if (!hasUserId) return true
  if (organizationsStatus === "idle" || organizationsStatus === "loading") return true
  if (organizationsStatus === "failed") return false
  if (organizationCount <= 0) return false
  return userOrgRoleStatus !== "succeeded" && userOrgRoleStatus !== "failed"
}

/**
 * Role for sidebar/profile badge — org Owner/Admin win over property collaborator role;
 * otherwise property access from collaborators API, then org membership role.
 */
export function getUserDisplayRole({
  organizationRole,
  collaboratorRows,
  propertyRole,
  orgRolePending = false,
} = {}) {
  if (orgRolePending) return null

  const orgKey = organizationRole ? normalizeRoleKey(organizationRole) : ""
  if (ORG_LEVEL_DISPLAY_ROLES.has(orgKey)) {
    return orgKey
  }

  const rows = Array.isArray(collaboratorRows) ? collaboratorRows : []
  if (rows.length > 0) {
    const accessLevel = rows[0]?.accessLevel
    if (accessLevel != null && String(accessLevel).trim() !== "") {
      return normalizeRoleKey(accessLevel)
    }
  }
  if (propertyRole != null && String(propertyRole).trim() !== "") {
    return normalizeRoleKey(propertyRole)
  }

  return orgKey || null
}

/** Org user-list role chip class (ViewAccount, OrganizationSettings). */
export function getOrgUserRoleChipClass(role) {
  if (!role) return "default"
  const chipClass = getRoleChipClass(role)
  if (chipClass !== "default") return chipClass
  return String(role)
    .trim()
    .replace(/^ROLE_/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .replace(/\s+/g, "_")
}
